import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import OCL from 'openchemlib/full';
import { useState, useEffect, memo } from 'react';
import { ResponsiveChart } from 'react-d3-utils';
import OCLnmr from 'react-ocl-nmr';
import { useChartData } from '../../context/ChartContext';
import { useDispatch } from '../../context/DispatchContext';
import NextPrev from '../../elements/NextPrev';
import useSpectrum from '../../hooks/useSpectrum';
import { useMoleculeEditor } from '../../modal/MoleculeStructureEditorModal';
import MoleculeHeader from './MoleculeHeader';
import MoleculePanelHeader from './MoleculePanelHeader';
import useAtomAssignment from './useAtomAssignment';
const styles = {
    panel: css({
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
    }),
    innerPanel: css({
        display: 'flex',
        flex: '1',
        flexDirection: 'column',
    }),
    molecule: css({
        flex: 1,
        minHeight: '100px',
    }),
    slider: css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
    }),
    items: css({
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    }),
};
function MoleculePanelInner(props) {
    const { zones, ranges, molecules: moleculesProp, moleculesView, activeTab, displayerMode, } = props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [molecules, setMolecules] = useState([]);
    const dispatch = useDispatch();
    const openMoleculeEditor = useMoleculeEditor();
    const { currentDiaIDsToHighlight, handleOnAtomHover, handleOnClickAtom, assignedDiaIDsMerged, } = useAtomAssignment({ zones, ranges, activeTab, displayerMode });
    useEffect(() => {
        if (moleculesProp) {
            setMolecules((prevMolecules) => {
                if (moleculesProp.length > prevMolecules.length) {
                    setCurrentIndex(molecules.length);
                }
                return moleculesProp;
            });
        }
    }, [molecules.length, moleculesProp]);
    function handleReplaceMolecule(molecule, molfile) {
        const { id, label } = molecule;
        dispatch({
            type: 'SET_MOLECULE',
            payload: { molfile, id, label },
        });
    }
    function moleculeIndexHandler(index) {
        setCurrentIndex(index);
    }
    return (_jsxs("div", { css: styles.panel, children: [_jsx(MoleculePanelHeader, { currentIndex: currentIndex, moleculesView: moleculesView, molecules: molecules, onOpenMoleculeEditor: () => openMoleculeEditor(), onMoleculeIndexChange: moleculeIndexHandler }), _jsx("div", { css: styles.innerPanel, children: _jsx("div", { css: styles.molecule, children: _jsx(ResponsiveChart, { children: ({ height, width }) => {
                            return (_jsx(NextPrev, { onChange: (slideIndex) => setCurrentIndex(slideIndex), defaultIndex: currentIndex, children: molecules && molecules.length > 0 ? (molecules.map((mol, index) => (_jsxs("div", { css: styles.items, children: [_jsx(MoleculeHeader, { currentMolecule: mol, molecules: molecules }), _jsx("div", { css: styles.slider, className: "mol-svg-container", onDoubleClick: () => openMoleculeEditor(mol), style: {
                                                backgroundColor: (index + 1) % 2 !== 0 ? '#fafafa' : 'white',
                                            }, children: _jsx(OCLnmr, { OCL: OCL, id: `molSVG${index}`, width: width, height: height - 60, molfile: mol.molfile || '', setMolfile: (molfile) => handleReplaceMolecule(mol, molfile), setSelectedAtom: handleOnClickAtom, atomHighlightColor: currentDiaIDsToHighlight &&
                                                    currentDiaIDsToHighlight.length > 0
                                                    ? 'red'
                                                    : '#FFD700', atomHighlightOpacity: 0.35, highlights: currentDiaIDsToHighlight &&
                                                    currentDiaIDsToHighlight.length > 0
                                                    ? currentDiaIDsToHighlight
                                                    : assignedDiaIDsMerged, setHoverAtom: handleOnAtomHover, showAtomNumber: moleculesView?.[mol.id]?.showAtomNumber || false }) })] }, mol.id)))) : (_jsx("div", { css: styles.slider, style: { height: '100%' }, onClick: () => openMoleculeEditor(), children: _jsx("span", { children: "Click to draw molecule" }) })) }));
                        } }) }) })] }));
}
const MemoizedMoleculePanel = memo(MoleculePanelInner);
const emptyData = { ranges: {}, zones: {} };
export default function MoleculePanel() {
    const { molecules, view: { molecules: moleculesView, spectra: { activeTab }, }, displayerMode, } = useChartData();
    const data = useSpectrum(emptyData);
    const ranges = data?.ranges || {};
    const zones = data?.zones || {};
    return (_jsx(MemoizedMoleculePanel, { molecules,
        moleculesView,
        displayerMode,
        activeTab,
        ranges,
        zones }));
}
